
import { Shield, Users, Building, Calendar, TrendingUp } from "lucide-react";
import StrategyStats from "./StrategyStats";
import { useEffect, useState } from "react";
import { Info } from "lucide-react";

interface StrategyCardDetailsProps {
  isPro: boolean;
  followerCount: number;
  pnlRatio: number | null | undefined;
  timespan: number | null;
  exchange: string;
  getExchangeLogo: (exchange: string) => string;
  winRate?: number | null;
  maxDrawdown?: number | null;
  tradesWon?: number | null;
  tradesLost?: number | null;
  aum?: number | null;
  description: string;
  performanceSince?: string;
  formatDate?: (dateString: string) => string;
  isComingSoon?: boolean;
}

const StrategyCardDetails = ({ 
  isPro,
  followerCount,
  pnlRatio,
  timespan,
  exchange,
  getExchangeLogo,
  winRate,
  maxDrawdown,
  tradesWon,
  tradesLost,
  aum,
  description,
  performanceSince,
  formatDate,
  isComingSoon = false
}: StrategyCardDetailsProps) => {
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  
  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);

  // Safe method to handle undefined or null pnlRatio
  const formatPnlRatio = (ratio: number | null | undefined) => {
    return ratio !== null && ratio !== undefined ? `${ratio.toFixed(2)}%` : 'N/A';
  };

  return (
    <div className={`p-4 space-y-3 flex-1 flex flex-col ${isPhemexLanding ? 'bg-gray-900 text-white' : 'bg-white'} ${isPro ? 'text-[#012846]' : ''} ${isComingSoon ? 'blur-[1px]' : ''}`}>
      <div className="flex items-center gap-2 text-sm">
        <Users className={`w-4 h-4 ${isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}`} />
        <span className={isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}>
          {followerCount.toLocaleString()} followers
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <TrendingUp className={`w-5 h-5 ${isPro ? 'text-[#F6C528]' : isPhemexLanding ? 'text-blue-500' : 'text-green-500'}`} />
        <span className={`font-semibold text-lg ${isPhemexLanding ? 'text-white' : 'text-[#0F1B2C]'}`}>
          {formatPnlRatio(pnlRatio)}
        </span>
        {timespan ? (
          <span className={`text-xs ml-2 ${isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}`}>
            <Calendar className="w-3 h-3 inline mr-1" />
            {timespan} days
          </span>
        ) : performanceSince && formatDate ? (
          <span className={`text-xs ml-2 ${isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}`}>
            <Calendar className="w-3 h-3 inline mr-1" />
            since {formatDate(performanceSince)}
          </span>
        ) : null}
      </div>
      
      <div className="flex items-center gap-2">
        <Building className={`w-4 h-4 ${isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}`} />
        <div>
          <img 
            src={getExchangeLogo(exchange)}
            alt={`${exchange} logo`}
            className="h-4 w-auto object-contain"
          />
        </div>
      </div>

      <StrategyStats
        winRate={winRate}
        maxDrawdown={maxDrawdown}
        tradesWon={tradesWon}
        tradesLost={tradesLost}
        aum={aum}
        pnlRatio={pnlRatio}
        timespan={timespan}
        isPro={isPro}
        infoIconColor="#555555"
      />

      <div className="flex-1">
        <p className={`text-sm ${isPhemexLanding ? 'text-gray-300' : 'text-[#4A5568]'}`}>
          {description}
        </p>
      </div>
    </div>
  );
};

export default StrategyCardDetails;
